import type { TFunction } from 'i18next';
import type { FormInstance } from 'antd';
import { Typography } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { IconBuildingBank, IconSettings, IconUser } from '@tabler/icons-react';

import type { EnsoFormItem } from '@ohpen/enso-ui';
import { ComplianceStatus } from '@pmp/api';

import type { CompliancePermissionsField } from './interfaces';
import { COMPLIANCE_PERMISSION_LEVELS, SETTABLE_COMPLIANCE_STATUSES, isPermissiveComplianceLevel } from './utils';
import styles from './styles.module.css';

const labels = 'lender.lender.labels';

type SelectOption = { label: string; value: string };

const getComplianceStatusOptions = (t: TFunction): SelectOption[] =>
  SETTABLE_COMPLIANCE_STATUSES.map(status => ({ value: status, label: t(`${labels}.${status}`, { defaultValue: status }) }));

const getCompliancePermissionOptions = (t: TFunction): SelectOption[] =>
  COMPLIANCE_PERMISSION_LEVELS.map(level => ({ value: level, label: t(`${labels}.${level}`, { defaultValue: level }) }));

const getComplianceCardColumns = (
  t: TFunction,
  statusOptions: SelectOption[],
  permissionOptions: SelectOption[],
  defaultStatusName: NamePath,
  permissionsField: CompliancePermissionsField,
  reviewPermissionName: NamePath,
): EnsoFormItem[] => [
  {
    title: t(`${labels}.initialComplianceStatus`),
    dataIndex: defaultStatusName,
    valueType: 'select',
    fieldProps: { options: statusOptions, allowClear: false },
    formItemProps: { rules: [{ required: true }] },
    colProps: { span: 24 },
  },
  {
    valueType: 'text',
    renderFormItem: () => (
      <Typography.Text type="secondary" strong>
        {t(`${labels}.complianceStatusPermissions`)}
      </Typography.Text>
    ),
    colProps: { span: 24 },
  },
  ...SETTABLE_COMPLIANCE_STATUSES.map(
    (status): EnsoFormItem => ({
      title: t(`${labels}.${status}`, { defaultValue: status }),
      dataIndex: [permissionsField, status],
      valueType: 'select',
      fieldProps: { options: permissionOptions, allowClear: false },
      formItemProps: { rules: [{ required: true }] },
      colProps: { span: 24 },
    }),
  ),
  {
    title: t(`${labels}.reviewRequiredPermission`),
    dataIndex: reviewPermissionName,
    valueType: 'select',
    fieldProps: { options: permissionOptions, allowClear: false },
    formItemProps: { rules: [{ required: true }] },
    colProps: { span: 24 },
  },
];

const getComplianceSettingsColumns = (t: TFunction): EnsoFormItem[] => [
  {
    title: t(`${labels}.reviewOnBmpChanges`),
    dataIndex: 'reviewOnBmpChangesEnabled',
    valueType: 'switch',
    tooltip: t(`${labels}.reviewOnBmpChangesTooltip`),
    colProps: { span: 24 },
  },
  {
    title: t(`${labels}.periodicCheck`),
    dataIndex: 'periodicCheckEnabled',
    valueType: 'switch',
    tooltip: t(`${labels}.periodicCheckTooltip`),
    colProps: { span: 12 },
  },
  {
    valueType: 'dependency',
    dependencies: ['periodicCheckEnabled'],
    columns: [
      {
        title: t(`${labels}.reviewInterval`),
        dataIndex: 'resetReviewRequiredFlagAfterMonths',
        valueType: 'digit',
        fieldProps: (form: FormInstance) => ({
          min: 1,
          max: 24,
          precision: 0,
          disabled: !form.getFieldValue('periodicCheckEnabled'),
          className: styles.fullWidthField,
        }),
        formItemProps: (form: FormInstance) => ({
          rules: [
            {
              required: !!form.getFieldValue('periodicCheckEnabled'),
              message: t(`${labels}.reviewIntervalRequired`),
            },
          ],
        }),
        colProps: { span: 12 },
      },
    ],
  },
];

export const getComplianceSchema = (t: TFunction): EnsoFormItem[] => {
  const statusOptions = getComplianceStatusOptions(t);
  const permissionOptions = getCompliancePermissionOptions(t);

  return [
    {
      valueType: 'dependency',
      dependencies: [
        ['firmPermissions', ComplianceStatus.PENDING],
        ['intermediaryPermissions', ComplianceStatus.PENDING],
      ],
      columns: [
        {
          valueType: 'alert',
          colProps: { span: 24 },
          fieldProps: { message: t(`${labels}.pendingPermissiveWarning`), type: 'warning', showIcon: true },
          formItemProps: (form: FormInstance) => ({
            hidden: !(
              isPermissiveComplianceLevel(form.getFieldValue(['firmPermissions', ComplianceStatus.PENDING])) ||
              isPermissiveComplianceLevel(form.getFieldValue(['intermediaryPermissions', ComplianceStatus.PENDING]))
            ),
          }),
        },
      ],
    },
    {
      valueType: 'group',
      variant: 'card',
      icon: <IconBuildingBank />,
      title: t(`${labels}.firmComplianceConfiguration`),
      colProps: { span: 12 },
      columns: getComplianceCardColumns(t, statusOptions, permissionOptions, ['defaultFirmStatus'], 'firmPermissions', [
        'firmToReviewPermissionLevel',
      ]),
    },
    {
      valueType: 'group',
      variant: 'card',
      icon: <IconUser />,
      title: t(`${labels}.intermediaryComplianceConfiguration`),
      colProps: { span: 12 },
      columns: getComplianceCardColumns(t, statusOptions, permissionOptions, ['defaultIntermediaryStatus'], 'intermediaryPermissions', [
        'intermediaryToReviewPermissionLevel',
      ]),
    },
    {
      valueType: 'group',
      variant: 'card',
      icon: <IconSettings />,
      title: t(`${labels}.complianceSettings`),
      colProps: { span: 12 },
      columns: getComplianceSettingsColumns(t),
    },
  ];
};
